import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./PlaylistViewer.css";
import {
  buildPlaylistItems,
  fetchYoutubeTitles,
  loadYoutubeIframeApi,
  youtubeItemId,
} from "../lib/youtubePlaylist";

const IFRAME_ALLOW =
  "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share";

// Stable identity so the items memo doesn't invalidate on every render.
const NO_EXTRA_ITEMS = [];

/**
 * A playlist player with an always-visible list of its videos.
 *
 * YouTube's own playlist embed hides its contents behind a collapsed overlay, so
 * we drive the player through the IFrame API instead and render the list
 * ourselves. `extraItems` lets non-YouTube videos be appended to that same list.
 */
export default function PlaylistViewer({
  playlistId,
  startVideoId = "",
  fallbackEmbedUrl = "",
  extraItems = NO_EXTRA_ITEMS,
  title = "Playlist",
}) {
  const hostRef = useRef(null);
  const playerRef = useRef(null);
  const videoIdsRef = useRef([]);
  const activeItemRef = useRef(null);
  const didJumpToStartRef = useRef(false);

  const [videoIds, setVideoIds] = useState([]);
  const [titles, setTitles] = useState({});
  const [activeId, setActiveId] = useState("");
  const [status, setStatus] = useState("loading");
  const [apiFailed, setApiFailed] = useState(false);

  const items = useMemo(
    () => buildPlaylistItems(videoIds, titles, extraItems),
    [videoIds, titles, extraItems]
  );

  const activeItem = items.find((item) => item.id === activeId) || items[0] || null;

  useEffect(() => {
    activeItemRef.current = activeItem;
  }, [activeItem]);

  useEffect(() => {
    const wrapper = hostRef.current;
    if (!playlistId || !wrapper) return undefined;

    let cancelled = false;

    // YT.Player replaces the node it is handed, so give it a throwaway child
    // rather than the ref'd wrapper itself.
    const mount = document.createElement("div");
    wrapper.appendChild(mount);

    const readPlaylist = (player) => {
      const ids = player.getPlaylist?.();
      if (!Array.isArray(ids) || ids.length === 0) return false;

      if (videoIdsRef.current.join() !== ids.join()) {
        videoIdsRef.current = ids;
        setVideoIds(ids);
        setStatus("ready");
      }

      return true;
    };

    loadYoutubeIframeApi()
      .then((YT) => {
        if (cancelled) return;

        const player = new YT.Player(mount, {
          width: "100%",
          height: "100%",
          playerVars: {
            listType: "playlist",
            list: playlistId,
            autoplay: 1,
            rel: 0,
            modestbranding: 1,
          },
          events: {
            // getPlaylist() is often still empty at onReady, so we read it here
            // too — the first state change always has it.
            onReady: () => readPlaylist(player),

            onStateChange: (event) => {
              if (!readPlaylist(player)) {
                if (event.data !== YT.PlayerState.UNSTARTED) setStatus("unavailable");
                return;
              }

              if (!didJumpToStartRef.current) {
                didJumpToStartRef.current = true;

                const startIndex = videoIdsRef.current.indexOf(startVideoId);
                if (startVideoId && startIndex > 0) {
                  player.playVideoAt(startIndex);
                  return;
                }
              }

              // Only follow the player while a YouTube video is the one on screen.
              if (activeItemRef.current && activeItemRef.current.kind !== "youtube") {
                return;
              }

              const index = player.getPlaylistIndex?.();
              const videoId = videoIdsRef.current[index];
              if (videoId) setActiveId(youtubeItemId(index, videoId));
            },

            // Fires for a single unplayable video too, so only treat it as a
            // dead playlist when we never got any contents at all.
            onError: () => {
              if (videoIdsRef.current.length === 0) setStatus("unavailable");
            },
          },
        });

        playerRef.current = player;
      })
      .catch(() => {
        if (!cancelled) setApiFailed(true);
      });

    return () => {
      cancelled = true;
      playerRef.current?.destroy?.();
      playerRef.current = null;
      videoIdsRef.current = [];
      didJumpToStartRef.current = false;
      // The player swapped `mount` for its own iframe, so clear the wrapper
      // instead — StrictMode remounts this effect and needs a clean host.
      wrapper.innerHTML = "";
    };
  }, [playlistId, startVideoId]);

  useEffect(() => {
    if (videoIds.length === 0) return undefined;

    let cancelled = false;

    fetchYoutubeTitles(videoIds).then((map) => {
      if (!cancelled) setTitles(map);
    });

    return () => {
      cancelled = true;
    };
  }, [videoIds]);

  const selectItem = useCallback((item) => {
    setActiveId(item.id);

    const player = playerRef.current;
    if (item.kind === "youtube") player?.playVideoAt?.(item.index);
    else player?.pauseVideo?.();
  }, []);

  // The API is blocked or offline — degrade to the plain embed we shipped before.
  if (apiFailed) {
    return (
      <div className="plv-wrap plv-wrap--solo">
        <div className="plv-stage">
          <iframe
            className="plv-frame"
            src={fallbackEmbedUrl}
            title={title}
            allow={IFRAME_ALLOW}
            allowFullScreen
            referrerPolicy="strict-origin-when-cross-origin"
          />
        </div>
      </div>
    );
  }

  const showPlayer = !activeItem || activeItem.kind === "youtube";

  const listSummary =
    status === "ready"
      ? `${items.length} ${items.length === 1 ? "video" : "videos"}`
      : status === "loading"
      ? "Loading videos…"
      : "Playlist contents unavailable";

  return (
    <div className="plv-wrap">
      <div className="plv-stage">
        <div
          ref={hostRef}
          className="plv-playerHost"
          style={{ visibility: showPlayer ? "visible" : "hidden" }}
        />

        {!showPlayer && (
          <iframe
            className="plv-frame"
            src={activeItem.embedUrl}
            title={activeItem.title}
            allow={IFRAME_ALLOW}
            allowFullScreen
            referrerPolicy="strict-origin-when-cross-origin"
          />
        )}
      </div>

      <div className="plv-list">
        <div className="plv-listInner">
          <div className="plv-listHead">{listSummary}</div>

          <ul className="plv-items" aria-label={`Videos in ${title}`}>
            {items.map((item, i) => (
              <li key={item.id}>
                <button
                  type="button"
                  className="plv-item"
                  onClick={() => selectItem(item)}
                  aria-current={item.id === activeItem?.id ? "true" : undefined}
                >
                  <span className="plv-thumbWrap">
                    {item.thumbnailUrl && (
                      <img
                        className="plv-thumb"
                        src={item.thumbnailUrl}
                        alt=""
                        loading="lazy"
                        referrerPolicy="no-referrer"
                      />
                    )}
                    <span className="plv-num">{i + 1}</span>
                  </span>

                  <span className="plv-title">{item.title}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
